import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Form, Input, Upload, Button, Typography, Alert, Steps,
  Descriptions, Divider, Progress, Table, Checkbox, Spin,
  Empty, Select, message, Modal,
} from 'antd';
import axios from 'axios';

const { Dragger } = Upload;
const { Title, Text } = Typography;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/* ═══════════════════════════════════════════
   DESIGN TOKENS — neutral greys + faint sky-blue accent
   ═══════════════════════════════════════════ */
const T = {
  ink: '#1f1f1f',
  sub: '#595959',
  muted: '#8c8c8c',
  faint: '#bfbfbf',
  border: '#d9d9d9',
  line: '#ececec',
  hairline: '#f0f0f0',
  panel: '#fafafa',
  head: '#f5f5f5',
  white: '#ffffff',
  blue: '#3f74ad',
  blueHover: '#4f86c6',
  blueActive: '#356296',
  blueSoft: '#eaf3fc',
  blueLine: '#d6e6f5',
};

const primaryBtn = { background: T.blue, borderColor: T.blue, color: '#fff' };

/* Spreadsheet grid CSS — tight bordered cells, no wrap, no row striping gaps */
const GRID_CSS = `
  .pts-form .ant-descriptions-bordered .ant-descriptions-item-label,
  .pts-form .ant-descriptions-bordered .ant-descriptions-item-content {
    padding: 7px 12px !important;
    border-color: ${T.line} !important;
  }
  .pts-form .ant-descriptions-bordered { border-color: ${T.line} !important; }
  .pts-form .ant-table-cell {
    padding: 7px 12px !important;
    border-color: ${T.line} !important;
    white-space: nowrap;
  }
  .pts-form .ant-table-thead > tr > th {
    background: ${T.head} !important;
    color: ${T.sub} !important;
    font-weight: 600 !important;
    border-color: ${T.line} !important;
  }
  .pts-form .ant-table-tbody > tr:hover > td { background: ${T.blueSoft} !important; }
`;

/* ═══════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════ */

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
};

const formatAmount = (val) => {
  if (!val && val !== 0) return '-';
  return `AED ${Number(val).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (val) => {
  if (!val) return '-';
  try {
    const d = new Date(val);
    return d.toLocaleDateString('en-GB', { timeZone: 'Asia/Dubai', day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return val; }
};

const getQualityLabel = (score) => {
  if (score >= 85) return 'Excellent';
  if (score >= 70) return 'Good';
  if (score >= 50) return 'Fair';
  return 'Needs Review';
};

/* ═══════════════════════════════════════════
   CATEGORIES  (color field kept but never rendered as colour)
   ═══════════════════════════════════════════ */

const CATEGORIES = [
  { key: 'material', title: 'Materials', description: 'Construction materials and industrial supplies', color: '#1a6ed8', requiredDocs: ['Invoice', 'Delivery Note'], requiredDocKeys: ['invoice', 'delivery_note'], recommendedDocs: [], recommendedDocKeys: [] },
  { key: 'labor_supply', title: 'Labor Supply', description: 'Manpower supply and staffing invoices', color: '#531dab', requiredDocs: ['Invoice', 'Timesheet'], requiredDocKeys: ['invoice', 'timesheet'], recommendedDocs: ['Attendance Sheet', 'Summary of Attendance'], recommendedDocKeys: ['attendance_sheet', 'summary_of_attendance'] },
  { key: 'sub_contractor', title: 'Sub-Contractors', description: 'Subcontractor services, contracts and claims', color: '#08979c', requiredDocs: ['Invoice', 'IPC'], requiredDocKeys: ['invoice', 'ipc'], recommendedDocs: ['Job Completion'], recommendedDocKeys: ['job_completion'] },
  { key: 'heavy_equipment_rental', title: 'Heavy Equipment Rental', description: 'Crane, excavator and heavy machinery hire', color: '#c41d7f', requiredDocs: ['Invoice', 'Timesheet'], requiredDocKeys: ['invoice', 'timesheet'], recommendedDocs: [], recommendedDocKeys: [] },
  { key: 'equipment_rental', title: 'Equipment Rental', description: 'Tools, generators and light equipment hire', color: '#d4380d', requiredDocs: ['Invoice', 'Timesheet'], requiredDocKeys: ['invoice', 'timesheet'], recommendedDocs: [], recommendedDocKeys: [] },
  { key: 'fees_and_charges', title: 'Fees and Charges', description: 'Government fees, permits and professional charges', color: '#389e0d', requiredDocs: ['Invoice'], requiredDocKeys: ['invoice'], recommendedDocs: [], recommendedDocKeys: [] },
  { key: 'repairs_and_maintenance', title: 'Repairs and Maintenance', description: 'Repair work, maintenance services and fixes', color: '#d48806', requiredDocs: ['Invoice'], requiredDocKeys: ['invoice'], recommendedDocs: ['Job Delivery Notes/Report', 'Hand-over Report'], recommendedDocKeys: ['job_delivery_report', 'handover_report'] },
];

const DOC_TYPE_LABELS = {
  invoice: 'Invoice', delivery_note: 'Delivery Note', timesheet: 'Timesheet',
  ipc: 'Interim Payment Certificate (IPC)', job_delivery_report: 'Job Delivery Notes/Report',
  handover_report: 'Hand-over Report', attendance_sheet: 'Attendance Sheet',
  summary_of_attendance: 'Summary of Attendance', job_completion: 'Job Completion Report',
  other: 'Other / Supporting Document',
};

/* Small neutral label used instead of coloured tags */
const Chip = ({ children, strong, style }) => (
  <span style={{
    display: 'inline-block', padding: '2px 8px', borderRadius: 2,
    border: `1px solid ${T.border}`, background: T.white,
    fontSize: 11, color: strong ? T.ink : T.sub, fontWeight: strong ? 600 : 500,
    lineHeight: '16px', ...style,
  }}>{children}</span>
);

/* ═══════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════ */

const InvoiceSubmissionForm = ({ username, onSuccess }) => {
  const [form] = Form.useForm();

  // Flow steps: batch → category → lpo_search → lpo_detail → upload → analyzing → review → submitting → success
  const [step, setStep] = useState('batch');
  const [selectedBatchNo, setSelectedBatchNo] = useState(null);
  const [batchList, setBatchList] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // LPO Search
  const [lpoSearchQuery, setLpoSearchQuery] = useState('');
  const [lpoSearchResults, setLpoSearchResults] = useState([]);
  const [lpoSearchLoading, setLpoSearchLoading] = useState(false);
  const [lpoSearched, setLpoSearched] = useState(false);
  const searchTimeoutRef = useRef(null);

  // Selected PO & Lines
  const [selectedPO, setSelectedPO] = useState(null);
  const [poLines, setPoLines] = useState([]);
  const [poHeader, setPoHeader] = useState(null);
  const [poTotals, setPoTotals] = useState(null);
  const [poLinesLoading, setPoLinesLoading] = useState(false);
  const [selectedLineIds, setSelectedLineIds] = useState([]);

  // Single PDF upload
  const [pdfFile, setPdfFile] = useState(null);

  // Analysis results
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState('');

  // Editable extracted data
  const [invoiceData, setInvoiceData] = useState({});
  const [metadata, setMetadata] = useState({});
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');

  // Submit
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [error, setError] = useState('');
  const [processingStep, setProcessingStep] = useState(0);
  const [analyzeStep, setAnalyzeStep] = useState(0);

  // Document preview (uploaded file shown alongside the review step)
  const [showPreview, setShowPreview] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Pre-submit confirmation
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);

  const catConfig = CATEGORIES.find(c => c.key === selectedCategory);

  // Build a local object URL for the uploaded file so it can be previewed
  // without waiting for an upload. Revoked automatically when the file changes.
  useEffect(() => {
    if (!pdfFile) { setPreviewUrl(null); return; }
    const url = URL.createObjectURL(pdfFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pdfFile]);

  /* ═══════════════════════════════════════════
     BATCH LOADING
     ═══════════════════════════════════════════ */

  const fetchBatches = useCallback(async () => {
    setBatchLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/procurement/batches`);
      if (res.data.success) {
        setBatchList(res.data.batches || []);
      }
    } catch (err) {
      console.error('Failed to load batches:', err);
    } finally {
      setBatchLoading(false);
    }
  }, []);

  useEffect(() => {
    if (step === 'batch') fetchBatches();
  }, [step, fetchBatches]);

  /* ═══════════════════════════════════════════
     LPO SEARCH
     ═══════════════════════════════════════════ */

  const searchLPOs = useCallback(async (query) => {
    setLpoSearchLoading(true);
    setLpoSearched(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/procurement/lpo-lookup/search`, {
        params: { q: query, limit: 25 },
      });
      if (res.data.success) setLpoSearchResults(res.data.results || []);
      else setLpoSearchResults([]);
    } catch (err) {
      console.error('LPO search failed:', err);
      setLpoSearchResults([]);
    } finally {
      setLpoSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    if (step !== 'lpo_search') return;
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (lpoSearchQuery.trim().length === 0) {
      searchTimeoutRef.current = setTimeout(() => searchLPOs(''), 300);
    } else if (lpoSearchQuery.trim().length >= 2) {
      searchTimeoutRef.current = setTimeout(() => searchLPOs(lpoSearchQuery.trim()), 400);
    }
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [lpoSearchQuery, step, searchLPOs]);

  useEffect(() => {
    if (step === 'lpo_search' && !lpoSearched) searchLPOs('');
  }, [step, lpoSearched, searchLPOs]);

  const selectPO = async (po) => {
    setSelectedPO(po);
    setPoLinesLoading(true);
    setStep('lpo_detail');
    setSelectedLineIds([]);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/procurement/lpo-lookup/${po.poaseq}/lines`);
      if (res.data.success) {
        setPoHeader(res.data.header);
        setPoLines(res.data.line_items || []);
        setPoTotals(res.data.totals);
        setSelectedLineIds((res.data.line_items || []).map(li => li.id));
      } else setError('Failed to load PO details');
    } catch { setError('Failed to load PO line items'); }
    finally { setPoLinesLoading(false); }
  };

  const confirmLinesAndProceed = () => {
    if (selectedLineIds.length === 0) { setError('Please select at least one line item'); return; }
    setError('');
    setStep('upload');
  };

  /* ═══════════════════════════════════════════
     ANALYZE PDF
     ═══════════════════════════════════════════ */

  const handleAnalyzePdf = async () => {
    if (!pdfFile) { setError('Please upload a PDF file'); return; }
    setAnalyzing(true);
    setAnalysisError('');
    setStep('analyzing');
    setAnalyzeStep(0);

    const formData = new FormData();
    formData.append('pdf_file', pdfFile);
    formData.append('category', selectedCategory);

    const t1 = setTimeout(() => setAnalyzeStep(1), 2000);
    const t2 = setTimeout(() => setAnalyzeStep(2), 5000);
    const t3 = setTimeout(() => setAnalyzeStep(3), 8000);

    try {
      const res = await axios.post(`${API_BASE_URL}/api/procurement/analyze-pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      const result = res.data;
      setAnalysisResult(result);
      setInvoiceData(result.invoice_data || {});
      setMetadata(result.metadata || {});
      setStep('review');
      if (!result.success) setAnalysisError(result.message || 'Analysis failed');
    } catch (err) {
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      setAnalysisError(err.response?.data?.message || err.message || 'Analysis failed');
      setStep('upload');
    } finally {
      setAnalyzing(false);
    }
  };

  /* ═══════════════════════════════════════════
     INLINE EDITING
     ═══════════════════════════════════════════ */

  const startEdit = (field, currentValue) => { setEditingField(field); setEditValue(currentValue || ''); };
  const saveEdit = (field, isMetadata = false) => {
    if (isMetadata) setMetadata(prev => ({ ...prev, [field]: editValue }));
    else setInvoiceData(prev => ({ ...prev, [field]: editValue }));
    setEditingField(null); setEditValue('');
  };
  const cancelEdit = () => { setEditingField(null); setEditValue(''); };

  const renderEditableValue = (field, value, isMetadata = false) => {
    if (editingField === field) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Input size="small" value={editValue} onChange={(e) => setEditValue(e.target.value)}
            onPressEnter={() => saveEdit(field, isMetadata)} style={{ borderRadius: 2, flex: 1, maxWidth: 280 }} autoFocus />
          <Button size="small" onClick={() => saveEdit(field, isMetadata)} style={{ ...primaryBtn, borderRadius: 2 }}>Save</Button>
          <Button size="small" type="text" onClick={cancelEdit} style={{ color: T.sub }}>Cancel</Button>
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ color: value ? T.ink : T.faint }}>{value || 'Not detected'}</span>
        <Button type="link" size="small" onClick={() => startEdit(field, value)}
          style={{ color: T.muted, padding: 0, height: 'auto', fontSize: 12 }}>Edit</Button>
      </div>
    );
  };

  /* ── amount editing with auto-recalculation (UAE VAT 5%) ── */
  const parseNum = (v) => {
    const n = parseFloat(String(v ?? '').replace(/[^\d.\-]/g, ''));
    return isNaN(n) ? null : n;
  };

  const saveAmountEdit = (field) => {
    const entered = parseNum(editValue);
    if (entered === null || entered < 0) { message.error('Enter a valid amount'); return; }

    let subtotal = parseNum(invoiceData.subtotal_amount) || 0;
    let vat      = parseNum(invoiceData.vat_amount) || 0;
    let total    = parseNum(invoiceData.total_amount) || 0;

    if (field === 'subtotal_amount') {
      subtotal = entered;
      vat   = Math.round(subtotal * 0.05 * 100) / 100;
      total = Math.round((subtotal + vat) * 100) / 100;
    } else if (field === 'vat_amount') {
      vat   = entered;                                   // keep subtotal, recompute total
      total = Math.round((subtotal + vat) * 100) / 100;
    } else { // total_amount
      total    = entered;                                // derive subtotal + VAT from total
      subtotal = Math.round((total / 1.05) * 100) / 100;
      vat      = Math.round((total - subtotal) * 100) / 100;
    }

    setInvoiceData(prev => ({
      ...prev,
      subtotal_amount: subtotal.toFixed(2),
      vat_amount: vat.toFixed(2),
      total_amount: total.toFixed(2),
    }));
    setEditingField(null); setEditValue('');
  };

  const renderEditableAmount = (field, value, opts = {}) => {
    if (editingField === field) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Input size="small" prefix="AED" value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onPressEnter={() => saveAmountEdit(field)}
            style={{ borderRadius: 2, maxWidth: 200 }} autoFocus />
          <Button size="small" onClick={() => saveAmountEdit(field)} style={{ ...primaryBtn, borderRadius: 2 }}>Save</Button>
          <Button size="small" type="text" onClick={cancelEdit} style={{ color: T.sub }}>Cancel</Button>
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Text strong={!!opts.strong} style={{ fontSize: opts.strong ? 16 : 13, color: value ? T.ink : T.faint }}>
          {value ? formatAmount(value) : 'Not detected'}
        </Text>
        <Button type="link" size="small" onClick={() => startEdit(field, value)}
          style={{ color: T.muted, padding: 0, height: 'auto', fontSize: 12 }}>Edit</Button>
      </div>
    );
  };

  /* ═══════════════════════════════════════════
     SUBMIT
     ═══════════════════════════════════════════ */

  const handleSubmit = async () => {
    if (analysisResult && !analysisResult.all_required_present) {
      setError(`Cannot submit: required documents missing (${(analysisResult.missing_doc_labels || []).join(', ')})`);
      return;
    }

    setSubmitting(true);
    setStep('submitting');
    setError('');
    setProcessingStep(0);

    const formData = new FormData();
    formData.append('pdf_file', pdfFile);
    formData.append('category', selectedCategory);
    formData.append('lpo_number', String(selectedPO?.poaseq || ''));
    formData.append('batch_no', String(selectedBatchNo));
    formData.append('project_code', poHeader?.fkbrcode || '');
    formData.append('lpo_amount', String(selectedPO?.poagross || poTotals?.gross_total || ''));
    formData.append('submitted_by', username);
    formData.append('confirmed_by', username);
    formData.append('invoice_data_json', JSON.stringify(invoiceData));
    formData.append('metadata_json', JSON.stringify(metadata));
    formData.append('detected_doc_types_json', JSON.stringify(analysisResult?.detected_doc_types || []));

    const t1 = setTimeout(() => setProcessingStep(1), 1500);
    const t2 = setTimeout(() => setProcessingStep(2), 3000);

    try {
      const res = await axios.post(`${API_BASE_URL}/api/procurement/submit`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000,
      });
      clearTimeout(t1); clearTimeout(t2);
      setProcessingStep(3);
      if (res.data.success) { setSubmitResult(res.data); setStep('success'); }
      else {
        setError(res.data.message || 'Submission failed');
        if (res.data.batch_received) setError(`Batch ${selectedBatchNo} has already been received by accounts. No further uploads allowed.`);
        setStep('review');
      }
    } catch (err) {
      clearTimeout(t1); clearTimeout(t2);
      setError(err.response?.data?.message || err.message || 'Submission failed.');
      setStep('review');
    } finally {
      setSubmitting(false);
    }
  };

  /* Pre-submit confirmation helpers */
  const openConfirm = () => { setConfirmChecked(false); setConfirmOpen(true); };
  const confirmRow = (label, value, emphasize = false) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '9px 14px', borderBottom: emphasize ? 'none' : `1px solid ${T.hairline}` }}>
      <Text style={{ fontSize: 12, color: T.sub }}>{label}</Text>
      <Text strong={emphasize} style={{ fontSize: emphasize ? 15 : 13, color: (value && value !== '-') ? T.ink : T.faint, textAlign: 'right' }}>{value || '-'}</Text>
    </div>
  );

  /* ═══════════════════════════════════════════
     RESET
     ═══════════════════════════════════════════ */

  const resetForm = () => {
    form.resetFields();
    setStep('batch'); setSelectedBatchNo(null); setSelectedCategory(null);
    setLpoSearchQuery(''); setLpoSearchResults([]); setLpoSearched(false);
    setSelectedPO(null); setPoLines([]); setPoHeader(null); setPoTotals(null);
    setSelectedLineIds([]); setPdfFile(null);
    setAnalysisResult(null); setAnalysisError('');
    setInvoiceData({}); setMetadata({}); setEditingField(null);
    setSubmitResult(null); setError(''); setProcessingStep(0); setAnalyzeStep(0);
  };

  /* Shared shell wrapper so the grid CSS is always present */
  const Shell = ({ children, max = 800 }) => (
    <div className="pts-form" style={{ maxWidth: max, margin: '0 auto' }}>
      <style>{GRID_CSS}</style>
      {children}
    </div>
  );

  const Panel = ({ children, style, pad = '16px 20px' }) => (
    <div style={{ border: `1px solid ${T.line}`, borderRadius: 4, background: T.white, marginBottom: 16, ...style }}>
      <div style={{ padding: pad }}>{children}</div>
    </div>
  );

  const StepHeader = ({ onBack, backLabel = 'Back', title, sub }) => (
    <Panel pad="12px 20px">
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        {onBack && <Button size="small" onClick={onBack} style={{ borderRadius: 2 }}>{backLabel}</Button>}
        {onBack && <Divider type="vertical" style={{ height: 24, margin: 0 }} />}
        <div style={{ flex: 1 }}>
          <Text strong style={{ fontSize: 14, color: T.ink }}>{title}</Text>
          {sub && <Text style={{ fontSize: 12, color: T.muted, display: 'block' }}>{sub}</Text>}
        </div>
        {selectedBatchNo != null && <Chip strong>Batch {selectedBatchNo}</Chip>}
      </div>
    </Panel>
  );

  /* Uploaded-document preview pane (uses the local object URL) */
  const DocumentPreview = () => {
    const fileExt = (pdfFile?.name || '').split('.').pop().toLowerCase();
    const fileMime = pdfFile?.type || '';
    const isPdfPreview = fileMime === 'application/pdf' || fileExt === 'pdf';
    const isImagePreview = ['jpg', 'jpeg', 'png', 'gif'].includes(fileExt)
      || ['image/jpeg', 'image/png', 'image/gif'].includes(fileMime);

    return (
      <div style={{
        flex: '1 1 400px', minWidth: 320,
        position: 'sticky', top: 16, alignSelf: 'flex-start',
      }}>
        <div style={{
          border: `1px solid ${T.line}`, borderRadius: 4, background: T.white,
          overflow: 'hidden', height: 'calc(100vh - 130px)', minHeight: 440,
          display: 'flex', flexDirection: 'column',
        }}>
          {/* header */}
          <div style={{ padding: '10px 14px', borderBottom: `1px solid ${T.hairline}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ minWidth: 0 }}>
              <Text strong style={{ fontSize: 13, color: T.ink, display: 'block' }}>Uploaded document</Text>
              <Text style={{ fontSize: 11, color: T.muted, display: 'block', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', maxWidth: 220 }}>{pdfFile?.name}</Text>
            </div>
            <Button size="small" onClick={() => previewUrl && window.open(previewUrl, '_blank')} style={{ borderRadius: 2 }}>Open</Button>
          </div>

          {/* body */}
          <div style={{ flex: 1, minHeight: 0, background: '#525659' }}>
            {!previewUrl ? (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spin /></div>
            ) : isPdfPreview ? (
              <iframe
                src={`${previewUrl}#toolbar=1&navpanes=0&view=FitH`}
                title="Uploaded document"
                style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
              />
            ) : isImagePreview ? (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto', padding: 12 }}>
                <img src={previewUrl} alt={pdfFile?.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 2 }} />
              </div>
            ) : (
              <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, background: T.panel, padding: 24, textAlign: 'center' }}>
                <Text style={{ color: T.sub, fontSize: 13 }}>Preview isn’t available for this file type.</Text>
                <Button onClick={() => previewUrl && window.open(previewUrl, '_blank')} style={{ ...primaryBtn, borderRadius: 2 }}>Open in new tab</Button>
              </div>
            )}
          </div>

          {/* footer hint */}
          <div style={{ padding: '8px 14px', borderTop: `1px solid ${T.hairline}`, background: T.panel }}>
            <Text style={{ fontSize: 11, color: T.muted }}>Scroll the document to verify the amounts, then edit any wrong field on the left.</Text>
          </div>
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════════════
     STEP 0: BATCH NUMBER SELECTION
     ═══════════════════════════════════════════ */

  if (step === 'batch') {
    const batchOptions = [];
    for (let i = 40; i <= 500; i++) {
      const info = batchList.find(b => b.batch_no === i);
      batchOptions.push({
        batch_no: i,
        status: info?.status || 'open',
        received_by: info?.received_by,
        submission_count: info?.submission_count || 0,
      });
    }

    return (
      <Shell max={700}>
        <Panel pad="16px 20px" style={{ marginBottom: 20 }}>
          <Text strong style={{ fontSize: 15, color: T.ink, display: 'block', marginBottom: 4 }}>Select Batch Number</Text>
          <Text style={{ fontSize: 13, color: T.muted }}>
            Choose the batch number for this invoice submission. Batches marked as received are locked.
          </Text>
        </Panel>

        {error && <Alert message={error} type="error" showIcon closable onClose={() => setError('')} style={{ marginBottom: 16, borderRadius: 4 }} />}

        <Panel pad="20px 24px">
          {batchLoading ? (
            <div style={{ padding: 40, textAlign: 'center' }}><Spin /><div style={{ marginTop: 12 }}><Text style={{ color: T.muted }}>Loading batches…</Text></div></div>
          ) : (
            <>
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ fontSize: 13, color: T.ink, display: 'block', marginBottom: 8 }}>Batch Number (40 – 500)</Text>
                <Select
                  showSearch
                  placeholder="Select or type batch number…"
                  style={{ width: '100%' }}
                  size="large"
                  value={selectedBatchNo}
                  onChange={(val) => setSelectedBatchNo(val)}
                  filterOption={(input, option) => String(option.value).includes(input)}
                  optionLabelProp="label"
                >
                  {batchOptions.map((b) => {
                    const isReceived = b.status === 'received';
                    return (
                      <Select.Option key={b.batch_no} value={b.batch_no} disabled={isReceived} label={`Batch ${b.batch_no}`}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontWeight: 600, color: isReceived ? T.faint : T.ink }}>Batch {b.batch_no}</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {b.submission_count > 0 && <Chip>{b.submission_count} inv</Chip>}
                            {isReceived && <Chip strong style={{ color: T.ink }}>Received · Locked</Chip>}
                          </div>
                        </div>
                      </Select.Option>
                    );
                  })}
                </Select>
              </div>

              {selectedBatchNo && (
                <div style={{ padding: '12px 16px', background: T.panel, borderRadius: 4, border: `1px solid ${T.line}`, marginBottom: 16 }}>
                  <Text strong style={{ fontSize: 15, color: T.ink }}>Batch {selectedBatchNo}</Text>
                  <Text style={{ fontSize: 12, color: T.muted, marginLeft: 10 }}>Open</Text>
                </div>
              )}

              <Button type="primary" size="large" block
                onClick={() => { if (!selectedBatchNo) { setError('Please select a batch number'); return; } setError(''); setStep('category'); }}
                disabled={!selectedBatchNo}
                style={{ ...primaryBtn, borderRadius: 4, height: 46, fontWeight: 600, fontSize: 14 }}>
                Continue to category
              </Button>
            </>
          )}
        </Panel>
      </Shell>
    );
  }

  /* ═══════════════════════════════════════════
     STEP 1: CATEGORY SELECTION
     ═══════════════════════════════════════════ */

  if (step === 'category') {
    return (
      <Shell max={800}>
        <StepHeader
          onBack={() => { setStep('batch'); setSelectedCategory(null); }}
          title={`Batch ${selectedBatchNo} — Select invoice category`}
          sub="Each category requires specific supporting documents."
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {CATEGORIES.map((cat) => (
            <div key={cat.key}
              onClick={() => { setSelectedCategory(cat.key); setStep('lpo_search'); }}
              style={{ border: `1px solid ${T.line}`, borderRadius: 4, background: T.white, cursor: 'pointer', padding: '16px 20px', transition: 'border-color .15s, background .15s' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.blueLine; e.currentTarget.style.background = T.blueSoft; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.line; e.currentTarget.style.background = T.white; }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <Text strong style={{ fontSize: 14, color: T.ink, display: 'block', marginBottom: 2 }}>{cat.title}</Text>
                  <Text style={{ fontSize: 12, color: T.muted, display: 'block', marginBottom: 8 }}>{cat.description}</Text>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {cat.requiredDocs.map(d => <Chip key={d} strong>{d} · Required</Chip>)}
                    {cat.recommendedDocs.map(d => <Chip key={d}>{d} · Recommended</Chip>)}
                  </div>
                </div>
                <Text style={{ color: T.faint, fontSize: 18 }}>›</Text>
              </div>
            </div>
          ))}
        </div>
      </Shell>
    );
  }

  /* ═══════════════════════════════════════════
     STEP 2: LPO SEARCH
     ═══════════════════════════════════════════ */

  if (step === 'lpo_search') {
    return (
      <Shell max={900}>
        {error && <Alert message={error} type="error" showIcon closable onClose={() => setError('')} style={{ marginBottom: 16, borderRadius: 4 }} />}
        <StepHeader
          onBack={() => { setStep('category'); setSelectedCategory(null); setLpoSearchQuery(''); setLpoSearchResults([]); setLpoSearched(false); }}
          title={`${catConfig?.title} — Select purchase order`}
          sub={`Required: ${catConfig?.requiredDocs.join(', ')}`}
        />
        <Panel>
          <Input size="large" placeholder="Search by PO number, supplier name, or project code"
            value={lpoSearchQuery} onChange={(e) => setLpoSearchQuery(e.target.value)} allowClear
            suffix={lpoSearchLoading ? <Spin size="small" /> : null}
            style={{ borderRadius: 4, height: 44, fontSize: 14 }} autoFocus />
        </Panel>
        <div style={{ border: `1px solid ${T.line}`, borderRadius: 4, background: T.white }}>
          {lpoSearchLoading && !lpoSearchResults.length ? (
            <div style={{ padding: 60, textAlign: 'center' }}><Spin /><div style={{ marginTop: 12 }}><Text style={{ color: T.muted }}>Searching purchase orders…</Text></div></div>
          ) : lpoSearchResults.length === 0 && lpoSearched ? (
            <Empty style={{ padding: 60 }} description={<Text type="secondary">No purchase orders found</Text>} />
          ) : (
            <div>
              <div style={{ padding: '10px 20px', borderBottom: `1px solid ${T.hairline}`, background: T.panel, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text strong style={{ fontSize: 13, color: T.ink }}>{lpoSearchQuery ? 'Search results' : 'Recent purchase orders'}</Text>
                <Chip>{lpoSearchResults.length} found</Chip>
              </div>
              {lpoSearchResults.map((po) => (
                <div key={po.poaseq} onClick={() => selectPO(po)} style={{ padding: '14px 20px', borderBottom: `1px solid ${T.hairline}`, cursor: 'pointer' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = T.blueSoft; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = T.white; }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
                    <Text strong style={{ fontSize: 13, color: T.ink, minWidth: 60 }}>{po.poaseq}</Text>
                    <Text strong style={{ fontSize: 13, color: T.ink, flex: 1, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{po.suppacc || 'Unknown Supplier'}</Text>
                    <Chip>{po.poastatusbydesc || 'Approved'}</Chip>
                    <Text strong style={{ fontSize: 13, color: T.ink, minWidth: 110, textAlign: 'right' }}>{formatAmount(po.poagross)}</Text>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', paddingLeft: 60 }}>
                    {po.fkbrcode && <Text style={{ fontSize: 11, color: T.muted }}>{po.fkbrcode?.length > 45 ? po.fkbrcode.substring(0, 45) + '…' : po.fkbrcode}</Text>}
                    {po.poadate && (<><Divider type="vertical" style={{ height: 12, margin: 0 }} /><Text style={{ fontSize: 11, color: T.muted }}>{formatDate(po.poadate)}</Text></>)}
                    <Divider type="vertical" style={{ height: 12, margin: 0 }} />
                    <Text style={{ fontSize: 11, color: T.muted }}>{po.line_count} line{po.line_count !== 1 ? 's' : ''}</Text>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Shell>
    );
  }

  /* ═══════════════════════════════════════════
     STEP 3: PO LINE ITEMS
     ═══════════════════════════════════════════ */

  if (step === 'lpo_detail') {
    const selectedLines = poLines.filter(li => selectedLineIds.includes(li.id));
    const selectedTotal = selectedLines.reduce((sum, li) => sum + (li.totalamountwithvat || 0), 0);
    const lineColumns = [
      { title: '', key: 'select', width: 44, render: (_, record) => <Checkbox checked={selectedLineIds.includes(record.id)} onChange={(e) => { if (e.target.checked) setSelectedLineIds(prev => [...prev, record.id]); else setSelectedLineIds(prev => prev.filter(id => id !== record.id)); }} /> },
      { title: 'Description', dataIndex: 'pobdescriptionnotrtf', key: 'desc', ellipsis: true, render: (t) => <Text style={{ fontSize: 12 }}>{t || '-'}</Text> },
      { title: 'Qty', dataIndex: 'pobqty', key: 'qty', width: 70, align: 'right', render: (v) => <Text style={{ fontSize: 12 }}>{v != null ? Number(v).toLocaleString() : '-'}</Text> },
      { title: 'Unit Price', dataIndex: 'pobunit', key: 'up', width: 120, align: 'right', render: (v) => <Text style={{ fontSize: 12 }}>{v != null ? formatAmount(v) : '-'}</Text> },
      { title: 'Total', dataIndex: 'totalamountwithvat', key: 'total', width: 130, align: 'right', render: (v) => <Text strong style={{ fontSize: 12, color: T.ink }}>{formatAmount(v)}</Text> },
    ];
    return (
      <Shell max={1100}>
        {error && <Alert message={error} type="error" showIcon closable onClose={() => setError('')} style={{ marginBottom: 16, borderRadius: 4 }} />}
        <StepHeader
          onBack={() => { setStep('lpo_search'); setSelectedPO(null); setPoLines([]); setPoHeader(null); }}
          title={`PO ${selectedPO?.poaseq} — Select line items`}
          sub={catConfig?.title}
        />
        {poLinesLoading ? <Panel><div style={{ padding: 60, textAlign: 'center' }}><Spin /></div></Panel> : (
          <>
            <div style={{ border: `1px solid ${T.line}`, borderRadius: 4, marginBottom: 16, overflow: 'hidden' }}>
              <Table columns={lineColumns} dataSource={poLines.map(li => ({ ...li, key: li.id }))} pagination={false} size="small" bordered scroll={{ x: 700 }} />
            </div>
            <Panel>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
                <div>
                  <Text style={{ fontSize: 13, color: T.sub }}>{selectedLineIds.length} of {poLines.length} lines selected</Text>
                  <Text strong style={{ fontSize: 15, color: T.ink, display: 'block' }}>Total: {formatAmount(selectedTotal)}</Text>
                </div>
                <Button type="primary" size="large" onClick={confirmLinesAndProceed} disabled={selectedLineIds.length === 0}
                  style={{ ...primaryBtn, borderRadius: 4, height: 44, fontWeight: 600 }}>Continue to upload</Button>
              </div>
            </Panel>
          </>
        )}
      </Shell>
    );
  }

  /* ═══════════════════════════════════════════
     STEP 4: UPLOAD
     ═══════════════════════════════════════════ */

  if (step === 'upload') {
    return (
      <Shell max={800}>
        {error && <Alert message={error} type="error" showIcon closable onClose={() => setError('')} style={{ marginBottom: 16, borderRadius: 4 }} />}
        <StepHeader
          onBack={() => setStep('lpo_detail')}
          title={`${catConfig?.title} — Upload document`}
          sub="Upload a single PDF containing all required documents"
        />
        <Panel pad="14px 20px">
          <Text strong style={{ fontSize: 13, color: T.ink, display: 'block', marginBottom: 8 }}>Document requirements</Text>
          <div style={{ marginBottom: 8 }}>
            <Text style={{ fontSize: 12, color: T.sub, fontWeight: 600, display: 'block', marginBottom: 4 }}>Required (must be included):</Text>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{catConfig?.requiredDocs.map(d => <Chip key={d} strong>{d}</Chip>)}</div>
          </div>
          {catConfig?.recommendedDocs.length > 0 && (
            <div>
              <Text style={{ fontSize: 12, color: T.muted, fontWeight: 600, display: 'block', marginBottom: 4 }}>Recommended (optional):</Text>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{catConfig?.recommendedDocs.map(d => <Chip key={d}>{d}</Chip>)}</div>
            </div>
          )}
        </Panel>
        <Panel pad="12px 20px">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center' }}>
            <div><Text style={{ fontSize: 11, color: T.muted, display: 'block' }}>PO Number</Text><Text strong style={{ fontSize: 14, color: T.ink }}>{selectedPO?.poaseq}</Text></div>
            <Divider type="vertical" style={{ height: 32, margin: 0 }} />
            <div><Text style={{ fontSize: 11, color: T.muted, display: 'block' }}>Supplier</Text><Text strong style={{ fontSize: 13, color: T.ink }}>{poHeader?.suppacc || '-'}</Text></div>
            <Divider type="vertical" style={{ height: 32, margin: 0 }} />
            <div><Text style={{ fontSize: 11, color: T.muted, display: 'block' }}>Lines</Text><Text strong style={{ fontSize: 13, color: T.ink }}>{selectedLineIds.length}</Text></div>
            <Divider type="vertical" style={{ height: 32, margin: 0 }} />
            <div><Text style={{ fontSize: 11, color: T.muted, display: 'block' }}>Batch</Text><Text strong style={{ fontSize: 13, color: T.ink }}>{selectedBatchNo}</Text></div>
          </div>
        </Panel>
        <Panel pad="20px 24px">
          {pdfFile ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px', background: T.white, border: `1px solid ${T.border}`, borderRadius: 4 }}>
              <div style={{ flex: 1 }}>
                <Text strong style={{ fontSize: 14, color: T.ink, display: 'block' }}>{pdfFile.name}</Text>
                <Text style={{ fontSize: 12, color: T.muted }}>{formatFileSize(pdfFile.size)}</Text>
              </div>
              <Chip strong>Ready</Chip>
              <Button type="text" onClick={() => setPdfFile(null)} style={{ color: T.sub }}>Remove</Button>
            </div>
          ) : (
            <Dragger showUploadList={false}
              beforeUpload={(f) => { if (f.size > 100 * 1024 * 1024) { setError('File too large. Maximum 100 MB.'); return Upload.LIST_IGNORE; } setPdfFile(f); return false; }}
              accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif"
              style={{ borderRadius: 4, borderColor: T.border, background: T.panel, padding: '28px 0' }}>
              <p style={{ fontSize: 15, color: T.ink, fontWeight: 600, marginBottom: 6 }}>Click or drag a file to upload</p>
              <p style={{ color: T.muted, fontSize: 13, marginBottom: 4 }}>Single PDF containing the invoice and all supporting documents</p>
              <p style={{ color: T.faint, fontSize: 12 }}>PDF, JPG, PNG accepted · Maximum 100 MB</p>
            </Dragger>
          )}
        </Panel>
        <Button type="primary" size="large" block onClick={handleAnalyzePdf} disabled={!pdfFile}
          style={{ ...primaryBtn, borderRadius: 4, height: 46, fontWeight: 600, fontSize: 14 }}>Analyze document</Button>
      </Shell>
    );
  }

  /* ═══════════════════════════════════════════
     STEP 4b: ANALYZING
     ═══════════════════════════════════════════ */

  if (step === 'analyzing') {
    return (
      <Shell max={560}>
        <div style={{ marginTop: 40 }}>
          <div style={{ border: `1px solid ${T.line}`, borderRadius: 4, overflow: 'hidden', background: T.white }}>
            <div style={{ padding: '36px 32px 24px', textAlign: 'center' }}>
              <Spin size="large" />
              <Title level={4} style={{ color: T.ink, margin: '20px 0 6px', fontWeight: 600 }}>Processing document</Title>
              <Text style={{ color: T.muted, fontSize: 14 }}>Reading and analyzing your uploaded file</Text>
            </div>
            <div style={{ margin: '0 32px 24px', padding: '12px 16px', background: T.panel, borderRadius: 4, border: `1px solid ${T.hairline}`, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text style={{ fontSize: 13, color: T.ink, display: 'block', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{pdfFile?.name || 'Document'}</Text>
                <Text style={{ fontSize: 11, color: T.muted }}>{formatFileSize(pdfFile?.size)} · Batch {selectedBatchNo}</Text>
              </div>
              <Chip>{catConfig?.title}</Chip>
            </div>
            <div style={{ padding: '0 32px 32px' }}>
              <Steps direction="vertical" size="small" current={analyzeStep} items={[
                { title: <Text style={{ fontSize: 13, color: analyzeStep >= 0 ? T.ink : T.faint, fontWeight: analyzeStep === 0 ? 600 : 400 }}>Uploading document</Text>, description: <Text style={{ fontSize: 12, color: T.muted }}>Transferring file to server</Text> },
                { title: <Text style={{ fontSize: 13, color: analyzeStep >= 1 ? T.ink : T.faint, fontWeight: analyzeStep === 1 ? 600 : 400 }}>Reading pages</Text>, description: <Text style={{ fontSize: 12, color: T.muted }}>Scanning and extracting text</Text> },
                { title: <Text style={{ fontSize: 13, color: analyzeStep >= 2 ? T.ink : T.faint, fontWeight: analyzeStep === 2 ? 600 : 400 }}>Classifying documents</Text>, description: <Text style={{ fontSize: 12, color: T.muted }}>Identifying document types</Text> },
                { title: <Text style={{ fontSize: 13, color: analyzeStep >= 3 ? T.ink : T.faint, fontWeight: analyzeStep === 3 ? 600 : 400 }}>Extracting data</Text>, description: <Text style={{ fontSize: 12, color: T.muted }}>Reading vendor details and amounts</Text> },
              ]} />
            </div>
            <div style={{ padding: '12px 32px', background: T.panel, borderTop: `1px solid ${T.hairline}` }}>
              <Text style={{ fontSize: 12, color: T.muted }}>This usually takes 15–30 seconds.</Text>
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  /* ═══════════════════════════════════════════
     STEP 5: REVIEW  (with side-by-side document preview)
     ═══════════════════════════════════════════ */

  if (step === 'review' && analysisResult) {
    const detectedTypes = analysisResult.detected_doc_types || [];
    const missingTypes = analysisResult.missing_doc_types || [];
    const allPresent = analysisResult.all_required_present;
    const pages = analysisResult.pages || [];
    const qualityScore = analysisResult.quality_score || 0;

    return (
      <Shell max={showPreview ? 1340 : 900}>
        {error && <Alert message={error} type="error" showIcon closable onClose={() => setError('')} style={{ marginBottom: 16, borderRadius: 4 }} />}
        {analysisError && <Alert message={analysisError} type="warning" showIcon closable onClose={() => setAnalysisError('')} style={{ marginBottom: 16, borderRadius: 4 }} />}

        {/* Header */}
        <Panel pad="12px 20px">
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <Button size="small" onClick={() => { setStep('upload'); setAnalysisResult(null); setAnalysisError(''); }} style={{ borderRadius: 2 }}>Re-upload</Button>
            <Divider type="vertical" style={{ height: 24, margin: 0 }} />
            <div style={{ flex: 1, minWidth: 180 }}>
              <Text strong style={{ fontSize: 14, color: T.ink }}>{catConfig?.title} — Review extracted data</Text>
              <Text style={{ fontSize: 12, color: T.muted, display: 'block' }}>Check the figures against the document on the right and edit any incorrect field</Text>
            </div>
            <Button size="small" onClick={() => setShowPreview(v => !v)} style={{ borderRadius: 2 }}>
              {showPreview ? 'Hide document' : 'Show document'}
            </Button>
            <Chip strong>Batch {selectedBatchNo}</Chip>
            <div style={{ textAlign: 'center' }}>
              <Progress type="circle" percent={qualityScore} size={40} strokeColor={T.blue} trailColor={T.hairline}
                format={(p) => <span style={{ fontSize: 11, fontWeight: 700, color: T.blue }}>{p}</span>} />
              <Text style={{ fontSize: 10, color: T.muted, display: 'block', marginTop: 2 }}>{getQualityLabel(qualityScore)}</Text>
            </div>
          </div>
        </Panel>

        {/* Split: left = data/edit, right = document preview */}
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>

          {/* ── LEFT COLUMN ── */}
          <div style={{ flex: '1 1 540px', minWidth: 0 }}>

            {/* Document Detection */}
            <Panel pad="16px 20px">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <Text strong style={{ fontSize: 14, color: T.ink }}>{allPresent ? 'All required documents found' : 'Missing required documents'}</Text>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                {catConfig?.requiredDocKeys.map(docKey => {
                  const found = detectedTypes.includes(docKey);
                  return (
                    <span key={docKey} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8, padding: '4px 10px', borderRadius: 2,
                      border: `1px solid ${found ? T.border : T.blue}`, background: found ? T.white : T.blueSoft, fontSize: 12, color: T.ink,
                    }}>
                      {DOC_TYPE_LABELS[docKey] || docKey}
                      <span style={{ fontSize: 11, fontWeight: 600, color: found ? T.sub : T.blue }}>{found ? 'Found' : 'Missing'}</span>
                    </span>
                  );
                })}
              </div>
              {!allPresent && (
                <Alert type="error" showIcon message="Required documents are missing"
                  description={<Text style={{ fontSize: 13 }}>Missing: <Text strong>{(analysisResult.missing_doc_labels || missingTypes.map(d => DOC_TYPE_LABELS[d] || d)).join(', ')}</Text></Text>}
                  style={{ borderRadius: 4, marginTop: 8 }} />
              )}
              {pages.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <Text style={{ fontSize: 12, color: T.muted, display: 'block', marginBottom: 6 }}>Page classification ({pages.length} pages):</Text>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {pages.map((p, idx) => <Chip key={idx}>Page {p.page_number}: {DOC_TYPE_LABELS[p.doc_type] || p.doc_type}{p.confidence ? ` · ${Math.round(p.confidence * 100)}%` : ''}</Chip>)}
                  </div>
                </div>
              )}
            </Panel>

            {/* Extracted Invoice Data */}
            <div style={{ border: `1px solid ${T.line}`, borderRadius: 4, background: T.white, marginBottom: 16 }}>
              <div style={{ padding: '12px 16px', borderBottom: `1px solid ${T.hairline}` }}>
                <Text strong style={{ fontSize: 14, color: T.ink }}>Extracted invoice data</Text>
              </div>

              {/* Amount verification note */}
              <Alert
                type="warning" showIcon
                message="Check the invoice amounts before submitting"
                description="AI-extracted figures aren't always correct. Verify Subtotal, VAT and Total against the document on the right and click Edit to fix any wrong value. Editing one amount auto-recalculates the other two (UAE VAT = 5%). If the invoice has VAT-exempt items, edit the VAT field last to override."
                style={{ borderRadius: 0, border: 'none', borderBottom: `1px solid ${T.hairline}` }}
              />

              <Descriptions bordered size="small" column={1}
                labelStyle={{ background: T.panel, fontWeight: 600, width: 180, color: T.sub, fontSize: 12 }}
                contentStyle={{ fontSize: 13, background: T.white }}>
                <Descriptions.Item label="Invoice Number">{renderEditableValue('invoice_number', invoiceData.invoice_number)}</Descriptions.Item>
                <Descriptions.Item label="Vendor Name">{renderEditableValue('vendor_name', invoiceData.vendor_name)}</Descriptions.Item>
                <Descriptions.Item label="Invoice Date">{renderEditableValue('invoice_date', invoiceData.invoice_date)}</Descriptions.Item>
                <Descriptions.Item label="Description">{renderEditableValue('description', invoiceData.description)}</Descriptions.Item>
                <Descriptions.Item label="Subtotal (Excl. VAT)">{renderEditableAmount('subtotal_amount', invoiceData.subtotal_amount)}</Descriptions.Item>
                <Descriptions.Item label="VAT Amount (5%)">{renderEditableAmount('vat_amount', invoiceData.vat_amount)}</Descriptions.Item>
                <Descriptions.Item label="Total Amount">{renderEditableAmount('total_amount', invoiceData.total_amount, { strong: true })}</Descriptions.Item>
                <Descriptions.Item label="Vendor TRN">{renderEditableValue('vendor_trn', invoiceData.vendor_trn)}</Descriptions.Item>
                <Descriptions.Item label="Vendor Email">{renderEditableValue('vendor_email', invoiceData.vendor_email)}</Descriptions.Item>
                <Descriptions.Item label="Vendor Phone">{renderEditableValue('vendor_phone', invoiceData.vendor_phone)}</Descriptions.Item>
                <Descriptions.Item label="LPO Reference">{renderEditableValue('lpo_reference', invoiceData.lpo_reference)}</Descriptions.Item>
                <Descriptions.Item label="Currency"><Text style={{ color: T.ink }}>{invoiceData.currency || 'AED'}</Text></Descriptions.Item>
              </Descriptions>
            </div>

            {/* Metadata */}
            {Object.values(metadata || {}).some(v => v) && (
              <div style={{ border: `1px solid ${T.line}`, borderRadius: 4, background: T.white, marginBottom: 16 }}>
                <div style={{ padding: '12px 16px', borderBottom: `1px solid ${T.hairline}` }}>
                  <Text strong style={{ fontSize: 14, color: T.ink }}>Supporting document details</Text>
                </div>
                <Descriptions bordered size="small" column={1}
                  labelStyle={{ background: T.panel, fontWeight: 600, width: 190, color: T.sub, fontSize: 12 }}
                  contentStyle={{ fontSize: 13, background: T.white }}>
                  {metadata.delivery_note_number != null && <Descriptions.Item label="Delivery Note No.">{renderEditableValue('delivery_note_number', metadata.delivery_note_number, true)}</Descriptions.Item>}
                  {metadata.delivery_note_date != null && <Descriptions.Item label="Delivery Note Date">{renderEditableValue('delivery_note_date', metadata.delivery_note_date, true)}</Descriptions.Item>}
                  {metadata.received_by != null && <Descriptions.Item label="Received By">{renderEditableValue('received_by', metadata.received_by, true)}</Descriptions.Item>}
                  {metadata.ipc_number != null && <Descriptions.Item label="IPC Number">{renderEditableValue('ipc_number', metadata.ipc_number, true)}</Descriptions.Item>}
                </Descriptions>
              </div>
            )}

            {/* Submit */}
            <Panel>
              <div style={{ padding: '12px 16px', background: T.panel, borderRadius: 4, border: `1px solid ${T.hairline}`, marginBottom: 16 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'center' }}>
                  <div><Text style={{ fontSize: 11, color: T.muted, display: 'block' }}>Batch</Text><Text strong style={{ fontSize: 13, color: T.ink }}>{selectedBatchNo}</Text></div>
                  <Divider type="vertical" style={{ height: 32, margin: 0 }} />
                  <div><Text style={{ fontSize: 11, color: T.muted, display: 'block' }}>Category</Text><Text strong style={{ fontSize: 13, color: T.ink }}>{catConfig?.title}</Text></div>
                  <Divider type="vertical" style={{ height: 32, margin: 0 }} />
                  <div><Text style={{ fontSize: 11, color: T.muted, display: 'block' }}>PO</Text><Text strong style={{ fontSize: 13, color: T.ink }}>{selectedPO?.poaseq}</Text></div>
                  <Divider type="vertical" style={{ height: 32, margin: 0 }} />
                  <div><Text style={{ fontSize: 11, color: T.muted, display: 'block' }}>Vendor</Text><Text strong style={{ fontSize: 13, color: T.ink }}>{invoiceData.vendor_name || '-'}</Text></div>
                  <Divider type="vertical" style={{ height: 32, margin: 0 }} />
                  <div><Text style={{ fontSize: 11, color: T.muted, display: 'block' }}>Total</Text><Text strong style={{ fontSize: 14, color: T.ink }}>{formatAmount(invoiceData.total_amount)}</Text></div>
                </div>
              </div>
              <Button type="primary" size="large" block onClick={openConfirm} disabled={!allPresent}
                style={{ ...primaryBtn, borderRadius: 4, height: 46, fontWeight: 600, fontSize: 14, ...(allPresent ? {} : { background: T.border, borderColor: T.border, color: '#fff' }) }}>
                {allPresent ? `Submit invoice to Batch ${selectedBatchNo}` : `Cannot submit — missing: ${(analysisResult.missing_doc_labels || []).join(', ')}`}
              </Button>
              {!allPresent && <div style={{ textAlign: 'center', marginTop: 10 }}><Button type="link" onClick={() => { setStep('upload'); setPdfFile(null); setAnalysisResult(null); }} style={{ color: T.sub }}>Upload a different document</Button></div>}
            </Panel>
          </div>

          {/* ── RIGHT COLUMN: uploaded document preview ── */}
          {showPreview && <DocumentPreview />}
        </div>

        {/* ── Pre-submit confirmation ── */}
        <Modal
          title={<span style={{ fontWeight: 700, color: T.ink }}>Confirm invoice details</span>}
          open={confirmOpen}
          onCancel={() => setConfirmOpen(false)}
          onOk={() => { setConfirmOpen(false); handleSubmit(); }}
          okText="Confirm & submit"
          cancelText="Go back & edit"
          okButtonProps={{ disabled: !confirmChecked, style: { ...primaryBtn, borderRadius: 4, fontWeight: 600, ...(confirmChecked ? {} : { background: T.border, borderColor: T.border, color: '#fff' }) } }}
          cancelButtonProps={{ style: { borderRadius: 4 } }}
          width={460}
          centered
          destroyOnClose
        >
          <Alert type="info" showIcon
            message="Please verify before submitting"
            description="Check that these details match the invoice. After submitting, it goes to accounts for review and can only be changed by uploading a revision."
            style={{ borderRadius: 4, marginBottom: 14 }} />

          <div style={{ border: `1px solid ${T.line}`, borderRadius: 4, overflow: 'hidden' }}>
            {confirmRow('Invoice Number', invoiceData.invoice_number)}
            {confirmRow('Vendor Name', invoiceData.vendor_name)}
            {confirmRow('Invoice Date', invoiceData.invoice_date)}
            {confirmRow('Subtotal (Excl. VAT)', invoiceData.subtotal_amount ? formatAmount(invoiceData.subtotal_amount) : '-')}
            {invoiceData.vat_amount != null && invoiceData.vat_amount !== '' &&
              confirmRow('VAT (5%)', formatAmount(invoiceData.vat_amount))}
            {confirmRow('Total Amount', invoiceData.total_amount ? formatAmount(invoiceData.total_amount) : '-', true)}
          </div>

          <Checkbox checked={confirmChecked} onChange={(e) => setConfirmChecked(e.target.checked)} style={{ marginTop: 14 }}>
            <Text style={{ fontSize: 13, color: T.ink }}>I confirm the above details are correct.</Text>
          </Checkbox>

          <div style={{ marginTop: 12, padding: '8px 12px', background: T.panel, border: `1px solid ${T.line}`, borderRadius: 4 }}>
            <Text style={{ fontSize: 12, color: T.muted }}>Reviewed &amp; confirmed by </Text>
            <Text strong style={{ fontSize: 12, color: T.ink }}>{username}</Text>
          </div>
        </Modal>
      </Shell>
    );
  }

  /* ═══════════════════════════════════════════
     STEP 6: SUBMITTING
     ═══════════════════════════════════════════ */

  if (step === 'submitting') {
    return (
      <Shell max={480}>
        <div style={{ marginTop: 40, border: `1px solid ${T.line}`, borderRadius: 4, background: T.white }}>
          <div style={{ textAlign: 'center', padding: '36px 32px 20px' }}>
            <Spin size="large" />
            <Title level={4} style={{ color: T.ink, margin: '20px 0 6px', fontWeight: 600 }}>Submitting invoice</Title>
            <Text style={{ color: T.muted, fontSize: 13 }}>Uploading to Batch {selectedBatchNo}</Text>
          </div>
          <div style={{ padding: '0 32px 32px' }}>
            <Steps direction="vertical" size="small" current={processingStep} items={[
              { title: 'Uploading document', description: 'Sending PDF to storage' },
              { title: 'Saving submission', description: 'Creating database record' },
              { title: 'Finalizing', description: 'Writing audit log' },
              { title: 'Complete', description: 'Submission created' },
            ]} />
          </div>
        </div>
      </Shell>
    );
  }

  /* ═══════════════════════════════════════════
     STEP 7: SUCCESS
     ═══════════════════════════════════════════ */

  if (step === 'success' && submitResult) {
    return (
      <Shell max={700}>
        <div style={{ border: `1px solid ${T.line}`, borderRadius: 4, background: T.white }}>
          <div style={{ textAlign: 'center', padding: '32px 32px 20px' }}>
            <Title level={4} style={{ color: T.ink, margin: '0 0 8px', fontWeight: 600 }}>Invoice submitted</Title>
            <Text style={{ color: T.muted, fontSize: 14 }}>
              {submitResult.submission_ref} submitted to Batch {submitResult.batch_no} for review.
            </Text>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, padding: '0 32px 24px' }}>
            <Button type="primary" onClick={resetForm} style={{ ...primaryBtn, borderRadius: 4, height: 40, fontWeight: 600 }}>Submit another invoice</Button>
            <Button onClick={() => onSuccess && onSuccess(submitResult.submission_ref)} style={{ borderRadius: 4, height: 40 }}>Back to dashboard</Button>
          </div>
          <Divider style={{ margin: 0 }} />
          <Descriptions bordered size="small" column={1}
            labelStyle={{ background: T.panel, fontWeight: 600, width: 190, color: T.sub, fontSize: 12 }}
            contentStyle={{ background: T.white, fontSize: 13 }}>
            <Descriptions.Item label="Submission Reference"><Text strong style={{ color: T.ink }}>{submitResult.submission_ref}</Text></Descriptions.Item>
            <Descriptions.Item label="Batch Number"><Text strong style={{ color: T.ink }}>Batch {submitResult.batch_no}</Text></Descriptions.Item>
            <Descriptions.Item label="PO Number"><Text strong>{selectedPO?.poaseq}</Text></Descriptions.Item>
            <Descriptions.Item label="Category">{catConfig?.title}</Descriptions.Item>
            <Descriptions.Item label="Vendor"><Text strong style={{ color: T.ink }}>{submitResult.vendor_name || '-'}</Text></Descriptions.Item>
            <Descriptions.Item label="Invoice Number">{submitResult.invoice_number || '-'}</Descriptions.Item>
            <Descriptions.Item label="Total Amount"><Text strong style={{ color: T.ink, fontSize: 15 }}>{formatAmount(submitResult.total_amount)}</Text></Descriptions.Item>
            <Descriptions.Item label="Status">Pending review</Descriptions.Item>
          </Descriptions>
        </div>
      </Shell>
    );
  }

  return <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>;
};

export default InvoiceSubmissionForm;